// locales.ts

import {
    ApiUserLocaleSchema,
    type ApiUserLocale,
} from "@bridge-applications/pb-types";

export type SupportedLocale = ApiUserLocale;

export const SUPPORTED_LOCALES = ApiUserLocaleSchema.options;

export const LANGUAGE_OPTIONS: ReadonlyArray<{
    code: SupportedLocale;
    label: string;
}> = [
    { code: "en", label: "English" },
    { code: "ru", label: "Русский" },
    { code: "uk", label: "Українська" },
    { code: "es", label: "Español" },
    { code: "pt-BR", label: "Português (Brasil)" },
    { code: "tr", label: "Türkçe" },
    { code: "ar", label: "العربية" },
    { code: "id", label: "Bahasa Indonesia" },
    { code: "de", label: "Deutsch" },
    { code: "fr", label: "Français" },
    { code: "pl", label: "Polski" },
    { code: "fa", label: "فارسی" },
    { code: "uz", label: "O‘zbekcha" },
    { code: "vi", label: "Tiếng Việt" },
    { code: "hi", label: "हिन्दी" },
    { code: "it", label: "Italiano" },
    { code: "ja", label: "日本語" },
    { code: "ko", label: "한국어" },
    { code: "zh-Hans", label: "简体中文" },
    { code: "zh-Hant", label: "繁體中文" },
];

const localeByLowerCase = new Map<string, SupportedLocale>(
    SUPPORTED_LOCALES.map((locale) => [locale.toLowerCase(), locale]),
);
const rtlLocales = new Set<SupportedLocale>(["ar", "fa"]);

export function normalizeLocale(
    value: string | null | undefined,
): SupportedLocale | null {
    const candidate = value?.trim().replace(/_/g, "-");
    if (!candidate) return null;

    let canonical = candidate;
    try {
        canonical = Intl.getCanonicalLocales(candidate)[0] ?? candidate;
    } catch {
        // Invalid provider/browser locale values fall through to basic parsing.
    }

    const exact = localeByLowerCase.get(canonical.toLowerCase());
    if (exact) return exact;

    const parts = canonical.toLowerCase().split("-");
    const language = parts[0];
    if (!language) return null;

    if (language === "pt") return "pt-BR";
    if (language === "zh") {
        return parts.some((part) => ["hant", "tw", "hk", "mo"].includes(part))
            ? "zh-Hant"
            : "zh-Hans";
    }

    return localeByLowerCase.get(language) ?? null;
}

export function resolveSupportedLocale(
    ...candidates: Array<string | null | undefined>
): SupportedLocale {
    for (const candidate of candidates) {
        const normalized = normalizeLocale(candidate);
        if (normalized) return normalized;
    }
    return "en";
}

export function isRtlLocale(locale: SupportedLocale): boolean {
    return rtlLocales.has(locale);
}
